import React from 'react';
import { ArrowUpRightIcon, AwardIcon, BriefcaseBusinessIcon, Code2Icon, DownloadIcon, GraduationCapIcon, LinkedinIcon, MailIcon, MapPinIcon, PhoneIcon, SparklesIcon, TargetIcon } from 'lucide-react';
import { openFullResumePdf } from './FullResumePdf';
type Experience = {
  company: string;
  sector: string;
  role: string;
  dates: string;
  description: string;
  bullets: string[];
  impact?: string[];
  featured?: boolean;
};
const primarySkills = ['Principal Product Designer', 'Staff Product Designer', 'Senior UX Designer', 'UX/UI Designer', 'Product Design', 'User Experience (UX)', 'Interaction Design', 'Design Systems', 'Human-Centered Design (HCD)', 'UX Research', 'UX Strategy', 'Cross-Functional Leadership', 'Information Architecture', 'Rapid Prototyping', 'Figma', 'Accessibility (WCAG)', 'Enterprise SaaS', 'Mobile & Responsive Design', 'AI Product Design', 'Data Visualization', 'Prototyping', 'Usability Testing', 'Enterprise UX', 'Agile / Scrum', 'Stakeholder Management', 'HTML / CSS / JavaScript'];
const competencies = ['User interviews & usability testing', 'Wireframing & interactive prototyping', 'Design systems & component libraries', 'Journey maps, task flows & UX audits', 'Responsive & mobile-first design', 'Research synthesis & design rationale', 'Developer handoff & design specifications', 'Cross-functional team leadership', 'Stakeholder presentations', 'Marketing & brand design'];
const experience: Experience[] = [{
  company: 'Rocket Communications',
  sector: 'U.S. Space Force · Government Defense',
  role: 'Staff UX Designer',
  dates: 'Jul 2021 — Present',
  description: 'Lead UX/UI design for Space Domain Awareness and Satellite Operations platforms funded by the U.S. Space Force.',
  bullets: ['Designed data-dense, mission-critical interfaces using the Astro UX Design System, reducing cognitive load and operator error.', 'Partner with engineers, product managers, and government stakeholders to turn complex requirements into intuitive workflows.', 'Deliver the full product design lifecycle: user flows, wireframes, high-fidelity UI, interactive prototypes, Figma specifications, and research-backed design rationale.', 'Contributed to Space Symposium 2024 branding and event design collateral.'],
  impact: ['75% fewer system errors', '60% faster operator training', '45% boost in team efficiency'],
  featured: true
}, {
  company: 'City National Rochdale',
  sector: 'Wealth Management · Financial Services',
  role: 'Sr. User Experience & Interaction Designer',
  dates: 'Sep 2017 — Jul 2021',
  description: 'Built and led a UX design team serving high-net-worth private wealth clients.',
  bullets: ['Redesigned the portfolio proposal platform with enhanced customization, increasing proposal production by 80%.', 'Established mobile-first product experiences that expanded accessibility and advisor productivity.', 'Led stakeholder interviews, UX research, and usability testing across product lines.', 'Delivered UX audits, journey maps, task flows, component libraries, and developer specifications in an Agile environment.'],
  impact: ['80% increase in user engagement', '65% higher customer satisfaction', '50% fewer manual tasks'],
  featured: true
}, {
  company: 'Walt Disney Animation Studios',
  sector: 'Entertainment · Animation Technology',
  role: 'Sr. Software Engineer, Web Technology',
  dates: 'Aug 2013 — Sep 2017',
  description: 'Designed and developed StoryPad, an iPad-native storyboard collaboration tool used by artists, editors, and directors.',
  bullets: ['Supported feature-film workflows including Frozen, Tangled, and Wreck-It Ralph.', 'Built image and video clip management, production tracking, personnel assignment, and document management systems.', 'Created internal production-network UI and WordPress plugins supporting hundreds of studio artists.', 'Bridged design and development to ensure pixel-accurate implementation across platforms.'],
  impact: ['40% gain in storyboard efficiency', '35% faster production timeline', 'Workflow optimization at scale'],
  featured: true
}, {
  company: 'Cedars-Sinai Medical Center',
  sector: 'Healthcare',
  role: 'Web Designer & Technology Specialist, Enterprise Information Systems',
  dates: 'Jan 2009 — Oct 2011',
  description: 'Designed campus-wide informational kiosk dashboards with real-time news and notifications for hospital guests.',
  bullets: ['Served as EIS Web Team liaison for internal staff and external vendors across print and digital channels.', 'Produced UX wireframes, responsive mobile designs, and visual design for corporate and internal websites.']
}, {
  company: 'Disney Interactive Media Group',
  sector: 'Gaming · Digital Entertainment',
  role: 'Web Designer / Developer',
  dates: 'Oct 2011 — Oct 2012',
  description: "Front-end visual designer for Disney Fairies' PixieHollow.com online gaming platform serving millions of users.",
  bullets: ['Delivered email campaign graphics, responsive and mobile-accessible designs, and dynamic calendar components.']
}, {
  company: 'CityGrid (IAC)',
  sector: 'Digital Media',
  role: 'Marketing Print/Web Designer',
  dates: 'Oct 2012 — Aug 2013',
  description: 'Design lead for marketing and sales collateral across print, web, and mobile.',
  bullets: ['Managed WordPress development, company branding, and sales-team marketing materials.']
}, {
  company: 'MTV Networks',
  sector: 'Media & Entertainment',
  role: 'Web Designer, Social Projects',
  dates: 'Nov 2007 — Jul 2008',
  description: 'Created 40+ branded online community environments for 50 Cent, GoNintendo, Pussycat Dolls, MTV, The Daily Show, Disney Radio, and The Hobbit.',
  bullets: []
}, {
  company: 'LoanToolbox.com',
  sector: 'Financial Services · E-Learning',
  role: 'Web Designer / Producer',
  dates: 'Sep 2002 — Nov 2007',
  description: 'Lead designer for 250+ animated tutorials, seminar speaker decks, email newsletters, and marketing materials.',
  bullets: ['Managed end-to-end design production for digital and print brand assets over a five-year tenure.']
}, {
  company: 'Jet Propulsion Laboratory (JPL / NASA)',
  sector: 'Government · Research',
  role: 'Graphic Designer',
  dates: 'May 2000 — May 2002',
  description: 'Provided production support for Documentation Services, interactive web applications, and multimedia design.',
  bullets: []
}];
const toolGroups = [{
  label: 'UX/UI Design & Research',
  items: 'Figma, Framer, Sketch, Adobe XD, InVision, Zeplin, Axure RP, Mural, Abstract'
}, {
  label: 'Adobe Creative Suite',
  items: 'Illustrator, Photoshop, InDesign, After Effects'
}, {
  label: 'Front-End',
  items: 'HTML5, CSS3, JavaScript, WordPress, Responsive Web Design'
}, {
  label: 'Collaboration',
  items: 'JIRA, Confluence, Slack, Zoom, Microsoft Teams, Google Workspace'
}];
const achievements = ['Designed mission-critical Space Force satellite operations UX, reducing workflow errors by 75%.', "Built and led City National Rochdale's UX team, increasing proposal output by 80%.", "Invented Disney Animation's StoryPad, accelerating film production by 35% through workflow optimization.", 'NNG Certified UX Professional, Certification #1034903.'];
function SectionLabel({
  children
}: {
  children: React.ReactNode;
}) {
  return <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#587a8b]" data-id="element-1">
      {children}
    </p>;
}
function MetricCard({
  value,
  label
}: {
  value: string;
  label: string;
}) {
  return <div className="border-l border-[#b9cbd0] pl-4" data-id="element-2">
      <p className="font-serif text-3xl font-semibold leading-none text-[#365767] pb-[4px]" data-id="element-3">
        {value}
      </p>
      <p className="mt-1 text-[11px] font-semibold uppercase leading-4 tracking-[0.08em] text-[#5d7883]" data-id="element-4">
        {label}
      </p>
    </div>;
}
function ExperienceItem({
  job
}: {
  job: Experience;
}) {
  return <article className={`relative py-8 ${job.featured ? '' : 'break-inside-avoid'}`} data-id="element-5">
      <div className="flex flex-col gap-3 border-l-2 border-[#b7cbd2] pl-5 sm:flex-row sm:items-start sm:justify-between" data-id="element-6">
        <div data-id="element-7">
          <h3 className="font-serif text-2xl font-semibold leading-tight text-[#365767]" data-id="element-8">
            {job.company}
          </h3>
          <p className="mt-1 text-[11px] font-bold uppercase tracking-[0.12em] text-[#668797]" data-id="element-9">
            {job.sector}
          </p>
          <p className="mt-3 text-sm font-bold text-[#3f6271]" data-id="element-10">{job.role}</p>
        </div>
        <p className="shrink-0 rounded-full border border-[#c7d5d7] bg-[#eff3f0] px-3 py-1.5 text-xs font-bold text-[#557482]" data-id="element-11">
          {job.dates}
        </p>
      </div>

      <ul className="mt-4 space-y-2 text-sm leading-6 text-[#4a504f]" data-id="element-12">
        {[job.description, ...job.bullets].map(bullet => <li key={bullet} className="flex gap-2.5" data-id="element-13">
            <span className="mt-[10px] h-1.5 w-1.5 shrink-0 rounded-full bg-[#7192a0]" aria-hidden="true" data-id="element-14" />
            <span data-id="element-15">{bullet}</span>
          </li>)}
      </ul>

      {job.impact && <div className="mt-5 flex flex-wrap gap-2" data-id="element-16">
          {job.impact.map(item => <span key={item} className="rounded-full bg-[#385d6e] px-3 py-1.5 text-xs font-bold text-white" data-id="element-17">
              {item}
            </span>)}
        </div>}
    </article>;
}
export function ResumePage() {
  return <main className="min-h-screen bg-[#e7e9e4] px-3 py-3 text-[#3d494a] sm:px-6 sm:py-8" data-id="element-18">
      <article className="resume-sheet mx-auto max-w-[1240px] overflow-hidden bg-[#faf7f0] shadow-[0_20px_60px_rgba(48,70,76,0.14)]" data-id="element-19">
        <header className="relative overflow-hidden border-b border-[#c8d6d6] bg-[#f8f5ee] px-6 py-9 sm:px-10 lg:px-14 lg:py-12" data-id="element-20">
          <div className="absolute -right-20 -top-32 h-[360px] w-[360px] rounded-full bg-[#e7eae4]" aria-hidden="true" data-id="element-21" />
          <div className="relative" data-id="element-22">
            <div className="flex flex-col justify-between gap-8 lg:flex-row lg:items-start" data-id="element-23">
              <div className="max-w-3xl" data-id="element-24">
                <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-[#5b7d8e] px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.16em] text-[#507285]" data-id="element-25">
                  <SparklesIcon size={13} data-id="element-26" />
                  Senior Product Design Leadership
                </div>
                <h1 className="font-serif text-5xl font-semibold leading-[0.9] tracking-tight text-[#365767] sm:text-6xl lg:text-7xl" data-id="element-27">
                  Jean M. Himebaugh
                </h1>
                <p className="mt-4 max-w-3xl text-sm font-bold uppercase leading-6 tracking-[0.1em] text-[#557684] sm:text-base" data-id="element-28">
                  Senior UX/UI Designer{' '}
                  <span className="px-1 text-[#92a8b0]" data-id="element-29">•</span> Product Design
                  Leader <span className="px-1 text-[#92a8b0]" data-id="element-30">•</span> NNG
                  Certified UX Specialist
                </p>
              </div>

              <div className="flex flex-col gap-3 text-sm text-[#405657] lg:items-end lg:text-right" data-id="element-31">
                <button type="button" onClick={openFullResumePdf} className="no-print inline-flex w-fit items-center gap-2 rounded-full bg-[#385d6e] px-4 py-2 text-xs font-bold text-white transition-colors hover:bg-[#2d4d5b] focus:outline-none focus:ring-2 focus:ring-[#7898a6] focus:ring-offset-2" data-id="element-32">
                  <DownloadIcon size={15} data-id="element-33" />
                  Download full resume (PDF)
                </button>
                <address className="grid gap-2 not-italic sm:grid-cols-2 lg:block" data-id="element-34">
                  <span className="flex items-center gap-2 lg:justify-end" data-id="element-35">
                    <MapPinIcon size={15} className="text-[#668797]" data-id="element-36" />
                    Los Angeles, CA
                  </span>
                  <a className="flex items-center gap-2 hover:text-[#365767] lg:justify-end" href="tel:8184682329" data-id="element-37">
                    <PhoneIcon size={15} className="text-[#668797]" data-id="element-38" />
                    (818) 468-2329
                  </a>
                  <a className="flex items-center gap-2 hover:text-[#365767] lg:justify-end" href="mailto:jeanthewebmachine@gmail.com" data-id="element-39">
                    <MailIcon size={15} className="text-[#668797]" data-id="element-40" />
                    jeanthewebmachine@gmail.com
                  </a>
                  <a className="flex items-center gap-2 hover:text-[#365767] lg:justify-end" href="https://linkedin.com/in/jeanhimebaugh" data-id="element-41">
                    <LinkedinIcon size={15} className="text-[#668797]" data-id="element-42" />
                    linkedin.com/in/jeanhimebaugh
                  </a>
                  <a className="flex items-center gap-2 hover:text-[#365767] lg:justify-end" href="https://jeanthewebmachine.com" data-id="element-43">
                    <ArrowUpRightIcon size={15} className="text-[#668797]" data-id="element-44" />
                    jeanthewebmachine.com
                  </a>
                </address>
              </div>
            </div>

            <div className="mt-9 grid gap-5 border-t border-[#d2dddd] pt-6 sm:grid-cols-3" data-id="element-45">
              <MetricCard value="20+" label="Years in UX, UI & Digital Design" data-id="element-46" />
              <MetricCard value="75%" label="Reduction in Space Force System Errors" data-id="element-47" />
              <MetricCard value="35%" label="Faster Disney Production Timeline" data-id="element-48" />
            </div>
          </div>
        </header>

        <div className="grid lg:grid-cols-[minmax(0,1fr)_310px]" data-id="element-49">
          <div className="px-6 py-9 sm:px-10 lg:px-14" data-id="element-50">
            <section aria-labelledby="summary-heading" data-id="element-51">
              <SectionLabel data-id="element-52">Executive Profile</SectionLabel>
              <h2 id="summary-heading" className="mt-2 font-serif text-3xl leading-tight text-[#365767]" data-id="element-53">
                Research-led UX leadership for complex, high-stakes products.
              </h2>
              <p className="mt-4 max-w-3xl text-[15px] leading-7 text-[#48504e]" data-id="element-54">
                Certified Senior UX/UI Designer with 20+ years leading{' '}
                <strong data-id="element-55">
                  product design, UX research, interaction design, design
                  systems, prototyping,
                </strong>{' '}
                and <strong data-id="element-56">developer handoff</strong> across enterprise,
                consumer, government, financial services, healthcare, and
                entertainment. Known for translating complex workflows into
                measurable, human-centered outcomes—from a{' '}
                <strong data-id="element-57">75% reduction in system errors</strong> for U.S. Space
                Force platforms to an{' '}
                <strong data-id="element-58">80% increase in user engagement</strong> for wealth
                management tools.
              </p>
            </section>

            <section className="mt-8 border-y border-[#cedada] py-6" aria-labelledby="skills-heading" data-id="element-59">
              <div className="flex items-center gap-2" data-id="element-60">
                <TargetIcon size={18} className="text-[#668797]" data-id="element-61" />
                <h2 id="skills-heading" className="text-xs font-bold uppercase tracking-[0.18em] text-[#557684]" data-id="element-62">
                  Recruiter Keyword Match
                </h2>
              </div>
              <div className="mt-4 flex flex-wrap gap-2" data-id="element-63">
                {primarySkills.map(skill => <span key={skill} className="rounded-full border border-[#bfd0d4] bg-[#f1f5f2] px-3 py-1.5 text-xs font-bold text-[#426777]" data-id="element-64">
                    {skill}
                  </span>)}
              </div>
            </section>

            <section className="mt-10" aria-labelledby="experience-heading" data-id="element-65">
              <div className="flex items-center gap-3" data-id="element-66">
                <BriefcaseBusinessIcon size={19} className="text-[#668797]" data-id="element-67" />
                <div data-id="element-68">
                  <SectionLabel data-id="element-69">Career History</SectionLabel>
                  <h2 id="experience-heading" className="mt-1 font-serif text-3xl text-[#365767]" data-id="element-70">
                    Professional Experience
                  </h2>
                </div>
              </div>

              <div className="mt-5 divide-y divide-[#d7e0de]" data-id="element-71">
                {experience.map(job => <ExperienceItem key={`${job.company}-${job.dates}`} job={job} data-id="element-72" />)}
              </div>
            </section>
          </div>

          <aside className="border-t border-[#c8d6d6] bg-[#eef1eb] px-6 py-9 sm:px-10 lg:border-l lg:border-t-0 lg:px-8" aria-label="Skills, certifications, and achievements" data-id="element-73">
            <section aria-labelledby="competencies-heading" data-id="element-74">
              <SectionLabel data-id="element-75">Capabilities</SectionLabel>
              <h2 id="competencies-heading" className="mt-1 font-serif text-2xl text-[#365767]" data-id="element-76">
                Core Competencies
              </h2>
              <ul className="mt-5 space-y-2.5 text-sm text-[#46595a]" data-id="element-77">
                {competencies.map(competency => <li key={competency} className="border-b border-[#d5dfdc] pb-2.5" data-id="element-78">
                    {competency}
                  </li>)}
              </ul>
            </section>

            <section className="mt-10" aria-labelledby="tools-heading" data-id="element-79">
              <div className="flex items-center gap-2" data-id="element-80">
                <Code2Icon size={17} className="text-[#668797]" data-id="element-81" />
                <SectionLabel data-id="element-82">Technical Toolkit</SectionLabel>
              </div>
              <h2 id="tools-heading" className="mt-1 font-serif text-2xl text-[#365767]" data-id="element-83">
                Tools & Technologies
              </h2>
              <div className="mt-5 space-y-5" data-id="element-84">
                {toolGroups.map(group => <div key={group.label} data-id="element-85">
                    <h3 className="text-sm font-bold text-[#3e6272]" data-id="element-86">
                      {group.label}
                    </h3>
                    <p className="mt-1 text-xs leading-5 text-[#566463]" data-id="element-87">
                      {group.items}
                    </p>
                  </div>)}
              </div>
            </section>

            <section className="mt-10 border-t border-[#c8d6d6] pt-8" aria-labelledby="certifications-heading" data-id="element-88">
              <div className="flex items-center gap-2" data-id="element-89">
                <GraduationCapIcon size={18} className="text-[#668797]" data-id="element-90" />
                <SectionLabel data-id="element-91">Credentials</SectionLabel>
              </div>
              <h2 id="certifications-heading" className="mt-1 font-serif text-2xl text-[#365767]" data-id="element-92">
                Education & Certifications
              </h2>
              <div className="mt-5 space-y-5 text-xs leading-5 text-[#4b5553]" data-id="element-93">
                <div data-id="element-94">
                  <p className="text-sm font-bold text-[#3e6272]" data-id="element-95">
                    Nielsen Norman Group (NNG)
                  </p>
                  <p className="mt-1 font-bold uppercase tracking-wide text-[#668797]" data-id="element-96">
                    2020
                  </p>
                  <p className="mt-1" data-id="element-97">
                    UX Certification — User Experience Design & Management
                    <br data-id="element-98" />
                    Certification #1034903
                  </p>
                </div>
                <div data-id="element-99">
                  <p className="text-sm font-bold text-[#3e6272]" data-id="element-100">
                    Interaction Design Foundation
                  </p>
                  <p className="mt-1 font-bold uppercase tracking-wide text-[#668797]" data-id="element-101">
                    2017
                  </p>
                  <p className="mt-1" data-id="element-102">
                    User Experience & Interaction Design
                    <br data-id="element-103" />
                    User Research & User-Centered Design
                  </p>
                </div>
                <div data-id="element-104">
                  <p className="text-sm font-bold text-[#3e6272]" data-id="element-105">
                    California State University, Northridge
                  </p>
                  <p className="mt-1 font-bold uppercase tracking-wide text-[#668797]" data-id="element-106">
                    1999–2002
                  </p>
                  <p className="mt-1" data-id="element-107">
                    B.S., Computer Science
                    <br data-id="element-108" />
                    (C++, Java, HTML)
                  </p>
                </div>
              </div>
            </section>

            <section className="mt-10 border-t border-[#c8d6d6] pt-8" aria-labelledby="achievements-heading" data-id="element-109">
              <div className="flex items-center gap-2" data-id="element-110">
                <AwardIcon size={18} className="text-[#668797]" data-id="element-111" />
                <SectionLabel data-id="element-112">Proof of Impact</SectionLabel>
              </div>
              <h2 id="achievements-heading" className="mt-1 font-serif text-2xl text-[#365767]" data-id="element-113">
                Career Achievements
              </h2>
              <ul className="mt-5 space-y-3 text-xs leading-5 text-[#4b5553]" data-id="element-114">
                {achievements.map(achievement => <li key={achievement} className="flex gap-2.5" data-id="element-115">
                    <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-[#7192a0]" aria-hidden="true" data-id="element-116" />
                    <span data-id="element-117">{achievement}</span>
                  </li>)}
              </ul>
            </section>
          </aside>
        </div>

        <footer className="border-t border-[#c8d6d6] bg-[#385d6e] px-6 py-4 text-center text-xs font-medium tracking-wide text-[#dce9e9]" data-id="element-118">
          Product Strategy · Research Insight · Human-Centered Systems ·
          Measurable Outcomes
        </footer>
      </article>
    </main>;
}